import { Fragment, memo } from 'react';

import reactStringReplace from 'react-string-replace';
import { sanitize, Tooltip } from '@honeycomb-npm/honeycomb-react';
import parse from 'html-react-parser';

import { textWithParams } from '../textWithParams';

type Props = {
    content?: string,
    id: string,
    params?: string[] | string,
    isParagraph?: boolean
    className?: string
    useTooltipIcon?: boolean
};

type TextNodeT = {
    data: string,
    type: string,
};

export const DynamicContent = memo(({
    content,
    id,
    params,
    isParagraph,
    className,
    useTooltipIcon,
}: Props): JSX.Element => {
    if (!content) return (<></>);

    const options = {
        replace: (domNode: unknown): JSX.Element | undefined => {
            const { type, data } = domNode as TextNodeT;

            let replacedResult;

            if (type === 'text') {
                const isToolTipText = /\(\[(.+?)\]\{(.+?)\}\)/g.test(data);

                if(isToolTipText) {
                    replacedResult = (
                        <>
                            {/* match a string which is wrapped by `([` and `})` */}
                            {reactStringReplace(data, /\(\[(.+?)}\)/g, (match) => {
                                // separate text and tooltipText by `]{`
                                const [text, tooltipText] = match.split(']{');

                                // Replace spacing with dash and covert to lower case
                                const mappedId = `${id}-${text.replace(/\s+/g, '-').toLowerCase()}`;

                                return (
                                    <Fragment key={mappedId}>
                                        <Tooltip
                                            id={mappedId}
                                            text={text}
                                            tooltipText={tooltipText}
                                            icon={useTooltipIcon}
                                        />
                                    </Fragment>
                                );
                            })}
                        </>
                    );
                }
            }

            return replacedResult;
        },
    };

    // Sanitise content after consuming params to make sure everything is safe
    const { __html: safeContent } = sanitize(textWithParams(content, params), true);

    // Check if any section of content contains p or dl
    const shouldWrapWithParagraph = isParagraph && !(/<p>[\s\S]*<\/p>|<dl>[\s\S]*<\/dl>|<ol>[\s\S]*<\/ol>|<ul>[\s\S]*<\/ul>/i.test(safeContent));

    const Tag = shouldWrapWithParagraph ?  'p' :  Fragment ;

    const tagProps = {
        id,
        className,
    };

    return (
        <Tag {...(shouldWrapWithParagraph && tagProps)}>
            {parse(safeContent, options)}
        </Tag>
    );
});

